"use client"

import type { ChainIdentifier } from "@opensea/graphql"
import { Avatar } from "@opensea/next-ui-kit"
import {
  CenterAligned,
  InlineFlex,
  NumberDisplay,
  TextBody,
  Tooltip,
} from "@opensea/ui-kit"
import { motion, type Transition } from "motion/react"
import { useState } from "react"
import type { Chain } from "../../chains/queries"
import { useChains } from "../../hooks/use-chains"

type StackedChainLogosSize = "sm" | "md"

type StackedChainLogosProps = {
  limit?: number
  size?: StackedChainLogosSize
  chains?: ChainIdentifier[]
}

const sizeConfig = {
  sm: { size: 24, overlap: 6, gap: 4, textSize: "xs" as const },
  md: { size: 36, overlap: 9, gap: 8, textSize: "xs" as const },
} as const

export const StackedChainLogos = ({
  limit = Number.POSITIVE_INFINITY,
  size = "md",
  chains: chainIdentifiers,
}: StackedChainLogosProps) => {
  const { size: avatarSize, overlap, gap, textSize } = sizeConfig[size]
  const [isHovered, setIsHovered] = useState(false)
  const { getLogo, getChain } = useChains()
  const defaultChains = useDefaultOrderedChains()

  const chains = chainIdentifiers
    ? chainIdentifiers
        .map(chainIdentifier => getChain(chainIdentifier))
        .filter(chain => chain !== undefined)
    : defaultChains

  const transition: Transition = {
    type: "spring",
    duration: 0.35,
    bounce: 0,
  }

  return (
    <InlineFlex
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {chains.slice(0, limit).map((chain, index) => (
        <Tooltip
          content={
            <CenterAligned className="text-center max-w-[480px]">
              <TextBody size="sm">{chain.name}</TextBody>
            </CenterAligned>
          }
          key={chain.identifier}
        >
          <motion.div
            animate={{
              marginLeft: index > 0 ? (isHovered ? gap : -overlap) : 0,
            }}
            className="relative will-change-transform"
            initial={false}
            layout
            transition={transition}
          >
            <Avatar
              {...getLogo(chain.identifier, { circle: true })}
              size={avatarSize}
            />
            <div className="absolute inset-0 border border-text-primary opacity-10 rounded-full" />
          </motion.div>
        </Tooltip>
      ))}
      {chains.length > limit && (
        <Tooltip
          content={
            <CenterAligned className="max-w-[480px] text-center">
              <TextBody size="sm">
                {chains
                  .slice(limit)
                  .map(chain => chain.name)
                  .join(", ")}
              </TextBody>
            </CenterAligned>
          }
        >
          <motion.div
            animate={{
              marginLeft: isHovered ? gap : -overlap,
            }}
            className="will-change-transform"
            initial={false}
            layout
            transition={transition}
          >
            <Avatar
              className="rounded-full border border-border-2 bg-bg-app"
              size={avatarSize}
            >
              <CenterAligned>
                <TextBody
                  color="text-secondary"
                  size={textSize}
                  style={{ fontSize: size === "sm" ? 9 : undefined }}
                  weight="semibold"
                >
                  <NumberDisplay
                    display="quantity"
                    prefix="+"
                    value={chains.length - limit}
                  />
                </TextBody>
              </CenterAligned>
            </Avatar>
          </motion.div>
        </Tooltip>
      )}
    </InlineFlex>
  )
}

const useDefaultOrderedChains = (): Chain[] => {
  const { chains: allChains, getChain } = useChains()
  const orderedChainIdentifiers = [
    "ethereum",
    "solana",
    "base",
    "abstract",
    "ape_chain",
    "arbitrum",
    "avalanche",
    "hyperevm",
    "ronin",
    "optimism",
    "polygon",
  ]
  const orderedChains = orderedChainIdentifiers
    .map(chainIdentifier => getChain(chainIdentifier))
    .filter(chain => chain !== undefined)
  const unorderedChains = allChains.filter(
    chain => !orderedChainIdentifiers.includes(chain.identifier),
  )
  return [...orderedChains, ...unorderedChains]
}
